import React from 'react';
import { styled } from '@compiled/react';
import Lozenge, { type ThemeAppearance } from '@atlaskit/lozenge';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { TruncateWithTooltipOnActive } from '@atlassian/jira-command-palette/src/common/ui/truncate-with-tooltip/truncate-with-tooltip-on-active/index.tsx';
import type { IssueProps } from '../types.tsx';

export const statusMap: Record<number, ThemeAppearance> = {
	1: 'default',
	2: 'default',
	3: 'success',
	4: 'inprogress',
} as const;

export const IssueView = ({
	issue: { issueKey, summary, projectName, status },
	isActive,
}: IssueProps) => (
	<Box xcss={searchResultContainerStyles}>
		<TopContainer>
			<TruncateWithTooltipOnActive isActive={!!isActive}>
				<IssueKey>{issueKey}</IssueKey>
				<IssueSummary active={isActive}>{summary}</IssueSummary>
			</TruncateWithTooltipOnActive>
		</TopContainer>
		{(status || projectName) && (
			<BottomContainer>
				{status ? (
					<Box xcss={statusStyles}>
						<Lozenge appearance={statusMap[status.statusCategoryId]} maxWidth={236}>
							{status.statusName}
						</Lozenge>
					</Box>
				) : null}
				<ProjectName active={isActive}>{projectName}</ProjectName>
			</BottomContainer>
		)}
	</Box>
);

const searchResultContainerStyles = xcss({
	width: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TopContainer = styled.div({
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: '24px',
	wordWrap: 'break-word',
	wordBreak: 'break-word',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IssueKey = styled.span({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtle'),
	marginRight: token('space.075'),
	display: 'inline',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IssueSummary = styled.span<{ active?: boolean }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	color: ({ active }) =>
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		active ? token('color.text') : token('color.text.subtle'),
	display: 'inline',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BottomContainer = styled.div({
	display: 'flex',
	marginTop: token('space.025'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtle'),
	font: token('font.body.UNSAFE_small'),
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: 'inherit',
});

const statusStyles = xcss({
	marginRight: 'space.150',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ProjectName = styled.div<{ active?: boolean }>({
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	maxWidth: '200px',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: '20px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	color: ({ active }) =>
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		active ? token('color.text.subtle') : token('color.text.subtlest'),
});
